"use client";

import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import {
  StytchPasswordReset,
  useStytch,
  useStytchSession,
} from "@stytch/react";
import { Link, useSearchParams } from "react-router-dom";
import { Products, StyleConfig, StytchLoginConfig } from "@stytch/vanilla-js";
import {
  ArrowLeft,
  CheckCircle,
  Envelope,
  GraduationCap,
  WarningCircle,
} from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";

const styles = {
  colors: {
    primary: "hsl(var(--meter-optimum))",
  },
  buttons: {
    primary: {
      backgroundColor: "hsl(var(--meter-optimum))",
      textColor: "hsl(var(--auth-white))",
      borderRadius: "4px",
      borderWidth: "0px",
      borderColor: "hsl(var(--meter-optimum))",
      fontSize: "16px",
      fontWeight: "600",
    },
  },
  container: {
    borderColor: "transparent",
    backgroundColor: "transparent",
  },
  inputs: {
    borderColor: "transparent",
    textColor: "hsl(var(--auth-text))",
    backgroundColor: "hsl(var(--auth-background))",
    borderRadius: "4px",
  },
  fontFamily: "Trenda",
  hideHeaderText: true,
} as StyleConfig;

const getConfig = (returnTo: string) => {
  const loginRedirectURL = `https://${import.meta.env.VITE_DOMAIN}/authenticate${returnTo !== "/" ? `?returnTo=${encodeURIComponent(returnTo)}` : ""}`;
  const resetPasswordRedirectURL = `https://${import.meta.env.VITE_DOMAIN}/authenticate/reset-password${returnTo !== "/" ? `?returnTo=${encodeURIComponent(returnTo)}` : ""}`;

  return {
    passwordOptions: {
      loginRedirectURL,
      resetPasswordRedirectURL,
      loginExpirationMinutes: 30,
      resetPasswordExpirationMinutes: 30,
    },
    emailMagicLinksOptions: {
      loginRedirectURL,
      loginExpirationMinutes: 30,
      signupRedirectURL: loginRedirectURL,
      signupExpirationMinutes: 30,
    },
    products: [Products.passwords, Products.emailMagicLinks],
    sessionOptions: {
      sessionDurationMinutes: 60 * 24 * 7,
    },
  } as StytchLoginConfig;
};
export default function ResetPasswordForm() {
  const { t } = useTranslation("auth");
  const stytchClient = useStytch();
  const [isSuccess, setIsSuccess] = useState(false);
  const [searchParams, _] = useSearchParams();
  const returnTo = searchParams.get("returnTo") || "/";

  const resetPasswordSchema = z.object({
    email: z.string().email(t("signup.validation.emailRequired")),
  });

  type ResetPasswordForm = z.infer<typeof resetPasswordSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<ResetPasswordForm>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const resetPasswordMutation = useMutation({
    mutationFn: (email: string) => {
      const redirectURL = `${import.meta.env.VITE_DOMAIN}/authenticate/reset-password${returnTo !== "/" ? `?returnTo=${encodeURIComponent(returnTo)}` : ""}`;
      return stytchClient.passwords.resetByEmailStart({
        email,
        reset_password_redirect_url: redirectURL,
      });
    },
    mutationKey: ["resetPassword"],
    onSuccess: () => {
      setIsSuccess(true);
    },
    onError: (error) => {
      console.error("Reset password error:", error);
    },
  });

  const stytch = useStytchSession();

  useEffect(() => {
    if (stytch.session !== null) {
      window.location.href = decodeURIComponent(returnTo);
    }
  }, [stytch.session, returnTo]);

  const onSubmit = (data: ResetPasswordForm) => {
    resetPasswordMutation.mutate(data.email);
  };

  const email = watch("email");

  if (
    searchParams.has("token") &&
    searchParams.get("stytch_token_type") === "reset_password"
  ) {
    return (
      <div className="flex flex-row w-full h-full">
        <div className="relative hidden w-full h-full px-10 sm:block py-9">
          <img
            src={"/assets/login-c.png"}
            className="object-cover w-full h-full rounded-[36px]"
          />
          <h1 className="absolute flex flex-row whitespace-nowrap gap-2 text-4xl font-semibold text-white bottom-24 right-12 text-[40px] xl:bottom-52 xl:right-44 xl:text-[79px] items-center">
            <GraduationCap size={60} />
            Teacher Tools
          </h1>
        </div>
        <main className="flex flex-col items-center justify-center w-full h-full">
          <StytchPasswordReset
            config={getConfig(returnTo)}
            styles={styles}
            passwordResetToken={searchParams.get("token") ?? ""}
          />
        </main>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="flex flex-row w-full h-full">
        <div className="relative hidden w-full h-full px-10 sm:block py-9">
          <img
            src={"/assets/login-c.png"}
            className="object-cover w-full h-full rounded-[36px]"
          />
          <h1 className="absolute flex flex-row whitespace-nowrap gap-2 text-4xl font-semibold text-white bottom-24 right-12 text-[40px] xl:bottom-52 xl:right-44 xl:text-[79px] items-center">
            <GraduationCap size={60} />
            Teacher Tools
          </h1>
        </div>
        <main className="flex flex-col items-center justify-center w-full h-full">
          <div className="flex items-center justify-center min-h-screen p-4 bg-gray-50">
            <Card className="w-full max-w-md">
              <CardHeader className="text-center">
                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-green-100 rounded-full">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <CardTitle className="text-xl">
                  {t("resetPassword.success.title")}
                </CardTitle>
                <CardDescription>
                  {t("resetPassword.success.description")}{" "}
                  <span className="font-medium">{email}</span>
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert>
                  <Envelope className="w-4 h-4" />
                  <AlertDescription>
                    {t("resetPassword.success.alertTitle")}
                  </AlertDescription>
                </Alert>
                <div className="text-center">
                  <Link
                    to={`/authenticate${returnTo !== "/" ? `?returnTo=${encodeURIComponent(returnTo)}` : ""}`}
                    className="text-sm text-green-600 hover:underline"
                  >
                    {t("backToLogin")}
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex flex-row w-full h-full">
      <div className="relative hidden w-full h-full px-10 sm:block py-9">
        <img
          src={"/assets/login-c.png"}
          className="object-cover w-full h-full rounded-[36px]"
        />
        <h1 className="absolute flex flex-row whitespace-nowrap gap-2 text-4xl font-semibold text-white bottom-24 right-12 text-[40px] xl:bottom-52 xl:right-44 xl:text-[79px] items-center">
          <GraduationCap size={60} />
          Teacher Tools
        </h1>
      </div>
      <main className="flex flex-col items-center justify-center w-full h-full">
        <div className="flex items-center justify-center min-h-screen p-4 bg-gray-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex items-center mb-4 space-x-2">
                <Link
                  to={`/authenticate${returnTo !== "/" ? `?returnTo=${encodeURIComponent(returnTo)}` : ""}`}
                  className="flex flex-row items-center gap-2 text-gray-500 hover:text-gray-700"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span className="text-sm text-gray-500">
                    {t("backToLogin")}
                  </span>
                </Link>
              </div>
              <CardTitle className="text-xl">
                {t("resetPassword.title")}
              </CardTitle>
              <CardDescription>
                {t("resetPassword.description")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">{t("resetPassword.emailLabel")}</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder={t("resetPassword.emailPlaceholder")}
                    {...register("email")}
                    className={errors.email ? "border-red-500" : ""}
                    disabled={resetPasswordMutation.isPending}
                  />
                  {errors.email && (
                    <p className="text-sm text-red-600">
                      {errors.email.message}
                    </p>
                  )}
                </div>

                {resetPasswordMutation.error && (
                  <Alert variant="destructive">
                    <WarningCircle className="w-4 h-4" />
                    <AlertDescription>
                      {resetPasswordMutation.error instanceof Error
                        ? resetPasswordMutation.error.message
                        : t("resetPassword.error.failed")}
                    </AlertDescription>
                  </Alert>
                )}

                <Button
                  type="submit"
                  value={"primary"}
                  className="w-full"
                  disabled={resetPasswordMutation.isPending}
                >
                  {resetPasswordMutation.isPending
                    ? t("resetPassword.button.sending")
                    : t("resetPassword.button.sendResetLink")}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
